import React, { useEffect, useRef } from 'react';
import { Pencil, X } from 'lucide-react';
import { RecipeDetails, RecipePhoto } from './RecipeDetails';
import type { Locale, RestaurantRecipe, SupplyItem } from './types';

/** Read-only recipe view. Native dialog provides focus containment and Escape support. */
export function RecipePreviewDialog({ recipe, items, locale, departmentName, actionsDisabled, onClose, onEdit, children }: {
  recipe: RestaurantRecipe; items: SupplyItem[]; locale: Locale; departmentName: string;
  actionsDisabled: boolean; onClose: () => void; onEdit: () => void; children: React.ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null), closeButton = useRef<HTMLButtonElement>(null);
  const titleId = React.useId();
  const text = (en: string, es: string) => locale === 'es' ? es : en;
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    element.showModal();
    closeButton.current?.focus({ preventScroll: true });
    return () => { element.close(); if (previous?.isConnected) previous.focus({ preventScroll: true }); };
  }, []);
  return <dialog ref={dialog} className="recipe-preview-dialog" aria-labelledby={titleId}
    onCancel={event => { event.preventDefault(); onClose(); }} onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <header className="recipe-preview-header"><div><span>{departmentName}{recipe.active === false ? ` · ${text('Archived', 'Archivada')}` : ''}</span><h2 id={titleId}>{recipe.name[locale]}</h2></div>
      <button ref={closeButton} type="button" className="icon-button" aria-label={text('Close recipe', 'Cerrar receta')} onClick={onClose}><X size={19} /></button>
    </header>
    <div className="recipe-preview-scroll" tabIndex={0} role="region" aria-label={text('Recipe details', 'Detalle de la receta')}>
      <RecipePhoto recipe={recipe} locale={locale} />
      <RecipeDetails recipe={recipe} items={items} locale={locale} showYield />
      {children}
    </div>
    <footer className="recipe-preview-footer">
      <button type="button" className="secondary-button" onClick={onClose}>{text('Close', 'Cerrar')}</button>
      <button type="button" className="primary-button" disabled={actionsDisabled} onClick={onEdit}><Pencil size={16} />{text('Edit recipe', 'Editar receta')}</button>
    </footer>
  </dialog>;
}
